import React, { useEffect, useState } from 'react';
import { InstitutionService } from '../../services/institution.service';
import { IInstitution, IInstitutionResponse } from '../../interfaces/institution.interface';
import { Modal, Button, Box, Tooltip, IconButton, Pagination, TextField, Typography } from '@mui/material';
import { helpWhy } from '../../help/why';
import { Delete, Add, CheckCircle, ToggleOn, ToggleOff, Visibility } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../context/ToastContext';
import ScrollableTable from '../common/ScrollableTable';

const InstitutionList = () => {
    const navigate = useNavigate();
    const toast = useToast();
    const [institutions, setInstitutions] = useState<IInstitution[]>([]);
    const [loading, setLoading] = useState(true);
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
    const [isApproveModalOpen, setIsApproveModalOpen] = useState(false);
    const [selectedInstitution, setSelectedInstitution] = useState<IInstitution | null>(null);
    const [approvingInstitution, setApprovingInstitution] = useState(false);
    // polish 6: approval creates ONE first administrator (a real person), not role-named accounts
    const [adminFirstName, setAdminFirstName] = useState('');
    const [adminLastName, setAdminLastName] = useState('');
    const [adminEmail, setAdminEmail] = useState('');
    const [pageInfo, setPageInfo] = useState({
        currentPage: 0,
        totalPages: 0,
        totalElements: 0
    });

    useEffect(() => {
        fetchInstitutions(0);
    }, []);

    const fetchInstitutions = async (page: number) => {
        try {
            setLoading(true);
            const response: IInstitutionResponse = await InstitutionService.getInstitutions(page, 10);
            setInstitutions(response.content);
            setPageInfo({
                currentPage: response.number,
                totalPages: response.totalPages,
                totalElements: response.totalElements
            });
        } catch (err) {
            toast.showApiError(err, 'Failed to load institutions');
            console.error('Error fetching institutions:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDeleteModal = (institution: IInstitution) => {
        setSelectedInstitution(institution);
        setIsDeleteModalOpen(true);
    };

    const handleCloseDeleteModal = () => {
        setIsDeleteModalOpen(false);
        setSelectedInstitution(null);
    };

    const handleDeleteInstitution = async () => {
        if (!selectedInstitution) return;

        try {
            await InstitutionService.deleteInstitution(selectedInstitution.id);
            await fetchInstitutions(pageInfo.currentPage);
            handleCloseDeleteModal();
        } catch (error) {
            console.error('Error deleting institution:', error);
            toast.showApiError(error, 'Failed to delete institution');
        }
    };

    const handleOpenApproveModal = (institution: IInstitution) => {
        setSelectedInstitution(institution);
        setAdminFirstName('');
        setAdminLastName('');
        setAdminEmail(institution.email ?? '');
        setIsApproveModalOpen(true);
    };

    const handleCloseApproveModal = () => {
        setIsApproveModalOpen(false);
        setSelectedInstitution(null);
    };

    const handleApproveInstitution = async () => {
        if (!selectedInstitution) return;

        try {
            setApprovingInstitution(true);
            await InstitutionService.approveInstitution(selectedInstitution.id, {
                firstName: adminFirstName.trim(), lastName: adminLastName.trim(), email: adminEmail.trim(),
            });
            toast.showSuccess(`Institution approved. The first administrator's login was e-mailed to ${adminEmail.trim()}.`);
            await fetchInstitutions(pageInfo.currentPage);
            handleCloseApproveModal();
        } catch (error) {
            console.error('Error approving institution:', error);
            toast.showApiError(error, 'Failed to approve institution');
        } finally {
            setApprovingInstitution(false);
        }
    };

    const handleToggleActive = async (institution: IInstitution) => {
        try {
            await InstitutionService.toggleInstitutionActive(institution.id);
            const newStatus = institution.isActive !== false ? 'deactivated' : 'activated';
            toast.showSuccess(`Institution "${institution.name}" has been ${newStatus} successfully`);
            await fetchInstitutions(pageInfo.currentPage);
        } catch (error) {
            toast.showApiError(error, 'Failed to toggle institution active status');
        }
    };

    const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
        fetchInstitutions(value - 1);
    };

    if (loading) {
        return <div data-testid="loading-state">Loading...</div>;
    }

    return (
        <div style={{ padding: '20px' }} data-help-page="institutions.list.page">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' , flexWrap: 'wrap', gap: '8px' }}>
                <h2>Institution List</h2>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<Add />}
                    data-help="institutions.list.create"
                    onClick={() => navigate('/institutions/create')}
                >
                    Create Institution
                </Button>
            </div>

            <div style={{ marginBottom: '20px' }} data-help="institutions.list.total">
                Total Institutions: {pageInfo.totalElements}
            </div>
            <ScrollableTable>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }} data-testid="institutions-table" data-help-section="institutions.list.table">
                <thead>
                    <tr>
                        <th style={tableHeaderStyle} data-help="institutions.list.col.name">Name</th>
                        <th style={tableHeaderStyle} data-help="institutions.list.col.code">Code</th>
                        <th style={tableHeaderStyle} data-help="institutions.list.col.description">Description</th>
                        <th style={tableHeaderStyle} data-help="institutions.list.col.email">Admin Email</th>
                        <th style={tableHeaderStyle} data-help="institutions.list.col.status">Status</th>
                        <th style={tableHeaderStyle} data-help="institutions.list.col.actions">Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {institutions.map(institution => (
                        <tr key={institution.id} data-testid={`institution-row-${institution.id}`}>
                            <td style={tableCellStyle}>{institution.name}</td>
                            <td style={tableCellStyle}>{institution.code}</td>
                            <td style={tableCellStyle}>{institution.description}</td>
                            <td style={tableCellStyle}>{institution.email || '—'}</td>
                            <td style={tableCellStyle}>
                                {!institution.approved ? (
                                    <span data-help="institutions.list.status.notApproved" style={{
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        backgroundColor: '#ffebee',
                                        color: '#c62828'
                                    }}>
                                        Not Approved
                                    </span>
                                ) : institution.isActive === false ? (
                                    <span data-help="institutions.list.status.deactivated" style={{
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        backgroundColor: '#fff3e0',
                                        color: '#e65100'
                                    }}>
                                        Deactivated
                                    </span>
                                ) : (
                                    <span data-help="institutions.list.status.approved" style={{
                                        padding: '4px 8px',
                                        borderRadius: '4px',
                                        backgroundColor: '#e8f5e9',
                                        color: '#2e7d32'
                                    }}>
                                        Approved
                                    </span>
                                )}
                            </td>
                            <td style={tableCellStyle}>
                                <Tooltip title="View Details" arrow>
                                    <IconButton
                                        size="small"
                                        onClick={() => navigate(`/institutions/${institution.id}`)}
                                        color="primary"
                                        aria-label="View Details"
                                        data-help="institutions.list.action.view"
                                    >
                                        <Visibility />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title={institution.approved ? "Institution already approved" : "Approve Institution"} arrow>
                                    <span data-help="institutions.list.action.approve">
                                        <IconButton
                                            size="small"
                                            onClick={() => handleOpenApproveModal(institution)}
                                            color="success"
                                            disabled={institution.approved}
                                            aria-label={institution.approved ? "Institution already approved" : "Approve Institution"}
                                        >
                                            <CheckCircle />
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                {institution.approved && (
                                    <Tooltip title={institution.isActive !== false ? "Deactivate Institution" : "Activate Institution"} arrow>
                                        <IconButton
                                            size="small"
                                            onClick={() => handleToggleActive(institution)}
                                            color={institution.isActive !== false ? "success" : "default"}
                                            aria-label={institution.isActive !== false ? "Deactivate Institution" : "Activate Institution"}
                                            data-help="institutions.list.action.toggle"
                                        >
                                            {institution.isActive !== false ? <ToggleOn /> : <ToggleOff />}
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <Tooltip title="Delete Institution" arrow>
                                    <IconButton
                                        size="small"
                                        onClick={() => handleOpenDeleteModal(institution)}
                                        color="error"
                                        aria-label="Delete Institution"
                                        data-help="institutions.list.action.delete"
                                    >
                                        <Delete />
                                    </IconButton>
                                </Tooltip>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
            </ScrollableTable>

            {pageInfo.totalPages > 1 && (
                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                    <Pagination
                        data-help="institutions.list.paging"
                        count={pageInfo.totalPages}
                        page={pageInfo.currentPage + 1}
                        onChange={handlePageChange}
                        color="primary"
                        showFirstButton
                        showLastButton
                    />
                </Box>
            )}

            {/* Delete Confirmation Modal */}
            <Modal
                open={isDeleteModalOpen}
                onClose={handleCloseDeleteModal}
                aria-labelledby="delete-confirmation-modal"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 400,
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                }} data-help-page="institutions.list.deleteDialog.page">
                    <h3>Delete Institution</h3>
                    <p>Are you sure you want to delete institution "{selectedInstitution?.name}"?</p>
                    <p style={{ color: 'red' }}>This action cannot be undone.</p>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button onClick={handleCloseDeleteModal} variant="outlined" data-help="institutions.list.deleteDialog.cancel">
                            Cancel
                        </Button>
                        <Button onClick={handleDeleteInstitution} variant="contained" color="error" data-help="institutions.list.deleteDialog.confirm">
                            Delete
                        </Button>
                    </Box>
                </Box>
            </Modal>

            {/* Approve Confirmation Modal */}
            <Modal
                open={isApproveModalOpen}
                onClose={handleCloseApproveModal}
                aria-labelledby="approve-confirmation-modal"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: 'min(560px, 92vw)',
                    bgcolor: 'background.paper',
                    boxShadow: 24,
                    p: 4,
                    borderRadius: 2,
                }} data-help-page="institutions.list.approveDialog.page">
                    <h3>Approve Institution</h3>
                    <p>Approve institution "{selectedInstitution?.name}"? This creates its first administrator and e-mails the login to them.</p>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 1 }}>
                        <TextField size="small" required label="First name" data-help="institutions.list.approveDialog.field.firstName" value={adminFirstName} sx={{ flex: '1 1 150px', maxWidth: 220 }}
                            inputProps={{ maxLength: 60 }}
                            onChange={(e) => setAdminFirstName(e.target.value)} />
                        <TextField size="small" required label="Last name" data-help="institutions.list.approveDialog.field.lastName" value={adminLastName} sx={{ flex: '1 1 150px', maxWidth: 220 }}
                            inputProps={{ maxLength: 60 }}
                            onChange={(e) => setAdminLastName(e.target.value)} />
                        <TextField size="small" required type="email" label="E-mail" data-help="institutions.list.approveDialog.field.email" value={adminEmail} sx={{ flex: '1 1 260px' }}
                            onChange={(e) => setAdminEmail(e.target.value)} />
                    </Box>
                    <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1 }}>
                        The username will be {selectedInstitution?.code}_{(adminFirstName.trim() || 'first').toLowerCase().replace(/[^a-z0-9]/g, '')}_{(adminLastName.trim() || 'last').toLowerCase().replace(/[^a-z0-9]/g, '')} (Latin letters only; a number is added if it is taken).
                    </Typography>
                    <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                        <Button
                            onClick={handleCloseApproveModal}
                            variant="outlined"
                            data-help="institutions.list.approveDialog.cancel"
                            disabled={approvingInstitution}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleApproveInstitution}
                            variant="contained"
                            color="success"
                            data-help="institutions.list.approveDialog.confirm"
                            {...helpWhy(!approvingInstitution && (!adminFirstName.trim() || !adminLastName.trim() || !adminEmail.trim()) ? { kind: 'status', message: 'Enter the first name, last name and e-mail of the first administrator.' } : null)}
                            disabled={approvingInstitution || !adminFirstName.trim() || !adminLastName.trim() || !adminEmail.trim()}
                        >
                            {approvingInstitution ? 'Approving...' : 'Approve'}
                        </Button>
                    </Box>
                </Box>
            </Modal>
        </div>
    );
};

const tableHeaderStyle = {
    backgroundColor: '#f4f4f4',
    padding: '12px',
    textAlign: 'left' as const,
    borderBottom: '2px solid #ddd'
};

const tableCellStyle = {
    padding: '12px',
    borderBottom: '1px solid #ddd'
};

export default InstitutionList;
